// Liquidaciones > Clientes — pantalla financiera funcional para TODOS los
// Stores/clientes Legacy con actividad en el período, no solo el piloto.
// Pipeline completo: legacy.envios -> legacy_store_map -> Store -> tarifa
// STORE -> CP/tier (tariff_postal_codes) -> precio base -> descuento ->
// precio final. Semana WYN (viernes a jueves), fecha_colecta.
//
// Un Store sin tarifa configurada IGUAL aparece acá (con "Sin tarifa" y sus
// envíos como pendientes) — Liquidaciones también sirve para detectar qué
// clientes faltan configurar. Nada pendiente se muestra como $0.
// Fuera de alcance acá: motor tarifario de Driver, premios, reportes,
// cierre, edición manual de precio (ver informe de la iteración).
import React, { useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import { downloadAuthenticatedFile } from "../lib/downloadFile";
import ClienteDetailModal from "../components/liquidaciones/ClienteDetailModal";

type Period = {
  key: string;
  from: string;
  to: string;
  label: string;
  fromDisplay: string;
  toDisplay: string;
};

type Summary = {
  totalDeliveries: number;
  pricedDeliveries: number;
  unresolvedDeliveries: number;
  totalAmount: number;
};

type ConfigStatus = "CONFIGURADO" | "CON_PENDIENTES" | "SIN_TARIFA";

type ClientRow = {
  storeId: number | string;
  storeName: string;
  legacyNames: string[];
  tariff: { id: number; name: string } | null;
  discount: number;
  deliveries: number;
  pricedDeliveries: number;
  unresolvedDeliveries: number;
  subtotalBeforeDiscount: number;
  total: number;
  configStatus: ConfigStatus;
};

type ClientesResponse = {
  ok: boolean;
  period: Period;
  summary: Summary;
  clients: ClientRow[];
  unmapped: { deliveries: number; distinctLegacyNames: string[] };
};

const pageWrapStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "16px" };
const headerRowStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" };
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "26px", fontWeight: 800, color: "var(--text-primary)", letterSpacing: "-0.02em" };
const subtitleStyle: React.CSSProperties = { margin: "6px 0 0 0", color: "var(--text-secondary)", fontSize: "14px" };
const periodBadgeStyle: React.CSSProperties = { display: "inline-block", padding: "4px 12px", borderRadius: "999px", border: "1px solid var(--border)", color: "var(--text-secondary)", fontSize: "13px", fontWeight: 600 };
const emptyStyle: React.CSSProperties = { padding: "32px 12px", color: "var(--text-secondary)", fontSize: "14px", textAlign: "center" };
const cardsRowStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px" };
const statLabelStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 };
const statValueStyle: React.CSSProperties = { fontSize: "26px", fontWeight: 800, color: "var(--text-primary)", marginTop: "6px" };
const sectionTitleStyle: React.CSSProperties = { margin: "0 0 10px 0", fontSize: "15px", fontWeight: 700, color: "var(--text-primary)" };
const toolbarStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", padding: "16px 16px 0 16px", flexWrap: "wrap" };
const toolbarControlsStyle: React.CSSProperties = { display: "flex", gap: "10px", flexWrap: "wrap" };
const searchFieldStyle: React.CSSProperties = { minWidth: "240px" };
const selectStyle: React.CSSProperties = {
  minHeight: "48px",
  padding: "0 14px",
  borderRadius: "14px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt, transparent)",
  color: "var(--text-primary)",
  fontSize: "14px",
};
const tableWrapStyle: React.CSSProperties = { overflowX: "auto", border: "1px solid var(--border)", borderRadius: "18px" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px", color: "var(--text-secondary)", fontSize: "12px", fontWeight: 700, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", background: "rgba(255,255,255,0.02)" };
const tdStyle: React.CSSProperties = { padding: "10px", color: "var(--text-primary)", fontSize: "13px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
const trClickableStyle: React.CSSProperties = { cursor: "pointer" };
const chipStyle: React.CSSProperties = { display: "inline-block", padding: "2px 8px", borderRadius: "999px", border: "1px solid var(--border)", color: "var(--text-secondary)", fontSize: "11px", marginRight: "4px", marginTop: "2px" };

const CONFIG_BADGE: Record<ConfigStatus, { label: string; color: string }> = {
  CONFIGURADO: { label: "Configurado", color: "var(--success, #2ea043)" },
  CON_PENDIENTES: { label: "Con pendientes", color: "#c99a1f" },
  SIN_TARIFA: { label: "Sin tarifa", color: "var(--danger, #d1242f)" },
};

function money(n: number): string {
  return `$${n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pct(n: number): string {
  return `${Math.round(n * 1000) / 10}%`;
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card padding="18px">
      <div style={statLabelStyle}>{label}</div>
      <div style={statValueStyle}>{value}</div>
    </Card>
  );
}

function ConfigBadge({ status }: { status: ConfigStatus }) {
  const b = CONFIG_BADGE[status];
  return (
    <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, border: `1px solid ${b.color}`, color: b.color }}>
      {b.label}
    </span>
  );
}

type SortKey = "name_asc" | "name_desc" | "deliveries_desc" | "deliveries_asc" | "total_desc" | "total_asc" | "tariff";

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "name_asc", label: "Nombre A-Z" },
  { value: "name_desc", label: "Nombre Z-A" },
  { value: "deliveries_desc", label: "Envíos ↓" },
  { value: "deliveries_asc", label: "Envíos ↑" },
  { value: "total_desc", label: "Facturación ↓" },
  { value: "total_asc", label: "Facturación ↑" },
  { value: "tariff", label: "Tarifa" },
];

function sortClients(clients: ClientRow[], sortKey: SortKey): ClientRow[] {
  const sorted = [...clients];
  switch (sortKey) {
    case "name_desc":
      return sorted.sort((a, b) => b.storeName.localeCompare(a.storeName));
    case "deliveries_desc":
      return sorted.sort((a, b) => b.deliveries - a.deliveries);
    case "deliveries_asc":
      return sorted.sort((a, b) => a.deliveries - b.deliveries);
    case "total_desc":
      return sorted.sort((a, b) => b.total - a.total);
    case "total_asc":
      return sorted.sort((a, b) => a.total - b.total);
    case "tariff":
      return sorted.sort((a, b) => (a.tariff?.name || "").localeCompare(b.tariff?.name || "") || a.storeName.localeCompare(b.storeName));
    case "name_asc":
    default:
      return sorted.sort((a, b) => a.storeName.localeCompare(b.storeName));
  }
}

export default function LiquidacionesClientes() {
  const { token } = useAuth();
  const [data, setData] = useState<ClientesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ id: number | string; name: string } | null>(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name_asc");
  const [downloadingAll, setDownloadingAll] = useState(false);

  const loadClientes = React.useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);

    return authFetch("/v1/liquidaciones/clientes", { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "Error al cargar Liquidaciones > Clientes");
        setData(payload);
      })
      .catch((e: any) => {
        setError(e.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve(loadClientes()).then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [loadClientes]);

  // Búsqueda client-side (~186 Stores ya están cargados en memoria — no
  // hace falta un endpoint de búsqueda nuevo, ver informe de la iteración).
  const filteredClients = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    const base = q
      ? data.clients.filter((c) => c.storeName.toLowerCase().includes(q) || c.legacyNames.some((n) => n.toLowerCase().includes(q)))
      : data.clients;
    return sortClients(base, sortKey);
  }, [data, search, sortKey]);

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      await downloadAuthenticatedFile("/v1/liquidaciones/clientes/export", token, "liquidaciones_clientes.zip");
    } catch (e: any) {
      alert(e.message || "No se pudo descargar el ZIP masivo.");
    } finally {
      setDownloadingAll(false);
    }
  };

  return (
    <div style={pageWrapStyle}>
      <div style={headerRowStyle}>
        <div>
          <h1 style={titleStyle}>Liquidaciones</h1>
          <p style={subtitleStyle}>Clientes — liquidación semanal por Store.</p>
        </div>
        {data && (
          <Button variant="secondary" onClick={handleDownloadAll} disabled={downloadingAll}>
            {downloadingAll ? "Generando…" : "Descargar todas"}
          </Button>
        )}
      </div>

      {loading && <Card>Cargando…</Card>}

      {!loading && error && (
        <Card>
          <span style={{ color: "var(--danger, #d1242f)" }}>{error}</span>
        </Card>
      )}

      {!loading && !error && data && (
        <>
          <div>
            <span style={periodBadgeStyle}>
              Semana WYN: {data.period.fromDisplay} → {data.period.toDisplay}
            </span>
          </div>

          <div style={cardsRowStyle}>
            <StatCard label="Envíos" value={String(data.summary.totalDeliveries)} />
            <StatCard label="Valorizados" value={String(data.summary.pricedDeliveries)} />
            <StatCard label="Pendientes" value={String(data.summary.unresolvedDeliveries)} />
            <StatCard label="Total a cobrar" value={money(data.summary.totalAmount)} />
          </div>

          <Card padding="0">
            <div style={toolbarStyle}>
              <h2 style={sectionTitleStyle}>Clientes ({filteredClients.length})</h2>
              <div style={toolbarControlsStyle}>
                <div style={searchFieldStyle}>
                  <Input placeholder="Buscar cliente…" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <select style={selectStyle} value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)}>
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ padding: "12px 16px 0 16px" }} />
            {filteredClients.length === 0 ? (
              <div style={emptyStyle}>{data.clients.length === 0 ? "Sin clientes mapeados todavía." : "Ningún cliente coincide con la búsqueda."}</div>
            ) : (
              <div style={{ ...tableWrapStyle, border: "none", borderRadius: 0 }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Cliente</th>
                      <th style={thStyle}>Tarifa</th>
                      <th style={thStyle}>Estado</th>
                      <th style={thStyle}>Envíos</th>
                      <th style={thStyle}>Valorizados</th>
                      <th style={thStyle}>Pendientes</th>
                      <th style={thStyle}>Descuento</th>
                      <th style={thStyle}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((c) => (
                      <tr
                        key={String(c.storeId)}
                        style={trClickableStyle}
                        onClick={() => setSelected({ id: c.storeId, name: c.storeName })}
                      >
                        <td style={tdStyle}>
                          {c.storeName}
                          {c.legacyNames.length > 1 && (
                            <div>
                              {c.legacyNames.map((n) => (
                                <span key={n} style={chipStyle}>{n}</span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={tdStyle}>{c.tariff ? c.tariff.name : "Sin tarifa"}</td>
                        <td style={tdStyle}><ConfigBadge status={c.configStatus} /></td>
                        <td style={tdStyle}>{c.deliveries}</td>
                        <td style={tdStyle}>{c.pricedDeliveries}</td>
                        <td style={tdStyle}>{c.unresolvedDeliveries}</td>
                        <td style={tdStyle}>{c.tariff ? pct(c.discount) : "—"}</td>
                        <td style={tdStyle}>{c.tariff ? money(c.total) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card padding="0">
            <div style={{ padding: "16px" }}>
              <h2 style={sectionTitleStyle}>Sin mapear</h2>
              <p style={{ ...subtitleStyle, marginBottom: "10px" }}>
                Clientes de Lightdata sin relación con un Store todavía — {data.unmapped.deliveries} envío(s) en {data.unmapped.distinctLegacyNames.length} nombre(s). Incluye variantes que necesitan confirmación manual antes de mapearse (ver reporte).
              </p>
              {data.unmapped.distinctLegacyNames.length === 0 ? (
                <div style={emptyStyle}>No hay clientes sin mapear en la semana actual.</div>
              ) : (
                <div>
                  {data.unmapped.distinctLegacyNames.map((n) => (
                    <span key={n} style={chipStyle}>{n}</span>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </>
      )}

      <ClienteDetailModal
        open={selected !== null}
        storeId={selected?.id ?? null}
        storeName={selected?.name ?? null}
        token={token}
        onClose={() => setSelected(null)}
        onOverrideChanged={loadClientes}
      />
    </div>
  );
}
